<template>
  <div class="contents full-height-minus-dropdown" v-if="opened">

    <div class="time-selection-columns">
      <div class="from-list scroll-height" v-if="rulesLoaded && timesLoading">

        <template v-if="timesLoading">
          <list-slot v-for="n in 5"
                    :key="'fromTimePlaceholder' + n"
                    :placeholder="true">
          </list-slot>
        </template>

      </div>
    </div>

    <div class="multi-time-container" :class="'child-blocks-' + typeDetails.length">
      <collapsible-pane v-for="type in typeDetails"
                        v-if="rulesLoaded && !timesLoading"
                        :key="'pane-' + type.id"
                        :identifier="type.id"
                        :header-title="type.getDescription()"
                        :expanded="type.expanded"
                        @toggle="handlePaneToggle">
        <div class="from-list adjusted-scroll-height">
          <list-slot v-for="timeSlot in type.startTimes"
                     :key="timeSlot.uniqueId + '-' + type.id"
                     :main-text="DateService.friendlyTime(timeSlot.time)"
                     :main-detail="getFriendlyEndTime(timeSlot.time, type)"
                     :centralise="true"
                     :disabled="timeSlot.isDisabled || !timeSlot.isValid"
                     :selected="timeSlot.selected"
                     v-on:click="toggleSelectedStartTime(type, timeSlot)"
                     no-arrow="true">
          </list-slot>
        </div>
      </collapsible-pane>
    </div>

    <div class="width-100 fixed-bottom flex-v-center flex-justify-right padding-v-half bg-main clickable"
         v-show="submittable" v-on:click="submit()">
      <span class="font-14 padding-r-half">Continue</span>
      <svg class="padding-r-1-em" width="8px" height="15px">
        <path d="M0 0 L8 7.5 L0 15 L0 13.5 L6.5 7.5 L0 1.5"></path>
      </svg>
    </div>
  </div>
</template>

<script>
import DateService from '../services/DateService'
import {TimesProvider} from '../services/TimesProvider'
import listSlot from './listSlot'
import RulesProvider from '../services/RulesProvider'
import PromptService from '../services/PromptService'
import CollapsiblePane from './collapsiblePane'
import MultiTimeBookingDetails from '../models/MultiTimeBookingDetails'

export default {
  name: 'multiTimeStage',
  props: [
    'venue',
    'pax',
    'date',
    'type',
    'additionalTypes',
    'value',
    'opened'
  ],
  components: {
    CollapsiblePane,
    listSlot
  },
  data () {
    return {
      DateService,
      typeDetails: [],
      bookingRules: [],
      timesLoading: true,
      rulesLoaded: false
    }
  },
  methods: {
    requestTimes () {
      this.requestRules()
      this.clearStartTimes()
      this.timesLoading = true

      this.timesProvider.requestTimesForMultipleTypes(this.getAdditionalTypeIds())
        .then((response) => {
          response.forEach((details, index) => {
            details.startTimes = details.startTimes.filter(this.shouldDisplayTime)
            details.expanded = index === 0

            // Include duration from rules
            const rules = this.getRulesForType(details.bookingType)
            details.duration = rules.maxDuration

            // Populate list of details
            this.typeDetails.push(new MultiTimeBookingDetails(details))
          })

          this.timesLoading = false
        })
    },

    requestRules () {
      this.rulesLoaded = false

      this.rulesProvider.requestMultipleRules(this.getAdditionalTypeIds())
        .then((rules) => {
          this.bookingRules = rules
          this.rulesLoaded = true
        })
    },

    clearStartTimes () {
      this.typeDetails = []
    },

    shouldDisplayTime (time) {
      return !time.isInPast
    },

    toggleSelectedStartTime (details, startTime) {
      if (startTime.isDisabled) return false

      // Unselect if it is already selected
      if (startTime.selected) {
        details.unselectTime()
      } else {
        details.selectTime(startTime)
        details.expanded = false

        // Expand next type without time
        let nextType = this.typeDetails.find(nextDetails => !nextDetails.hasTimeSelected())

        if (nextType) {
          nextType.expanded = true
        }
      }

      this.updateAvailableTimes()
    },

    getFriendlyEndTime (time, typeDetails) {
      const endTime = DateService.addMinutesToTime(typeDetails.duration, time)

      return DateService.friendlyTime({
        hours: endTime.getHours(),
        minutes: endTime.getMinutes()
      })
    },

    getRulesForType (bookingType) {
      // Find rules for type
      let typeRules = null
      this.bookingRules.forEach((bookingRules) => {
        if (bookingRules.type === bookingType) {
          typeRules = bookingRules
        }
      })

      return typeRules
    },

    handlePaneToggle (identifier) {
      // Find the pane that matches the identifier
      this.typeDetails.forEach((details) => {
        if (details.id === identifier) {
          // Toggle the expanded status
          details.expanded = !details.expanded
        } else {
          // Close other panes
          details.expanded = false
        }
      })
    },

    submit () {
      this.$emit('input', this.typeDetails)
    },

    updateAvailableTimes () {
      // Split details into those with times selected and those without
      let typesWithTimes = this.typeDetails.filter((details) => {
        return details.hasTimeSelected()
      })

      let typesWithoutTimes = this.typeDetails.filter((details) => {
        return !details.hasTimeSelected()
      })

      // Re-enable all times before blocking off times (COLLINS-10736)
      typesWithoutTimes.forEach((type) => {
        type.enableAllTimes()
      })

      typesWithTimes.forEach((type) => {
        // Calculate time windows to disable
        const timeSlot = type.getSelectedTime()

        const startTime = DateService.addMinutesToTime(type.bufferBefore * -1, timeSlot.time)
        const endTime = DateService.addMinutesToTime(type.duration + type.bufferAfter, timeSlot.time)

        // Disable time regions for non-selected booking types
        typesWithoutTimes.forEach((otherType) => {
          otherType.disableTimeWindow(startTime, endTime)
        })
      })
    },

    getAdditionalTypeIds () {
      return this.$root.additionalTypes.map((additionalType) => additionalType.id)
    }

  },

  mounted () {
    this.timesProvider = new TimesProvider(this.$root.booking)

    this.timesProvider.setUrlOptions({
      source: this.$root.dmnOptions.from
    })

    this.rulesProvider = new RulesProvider(this.$root.booking)

    this.rulesProvider.setUrlOptions({
      source: this.$root.dmnOptions.from
    })

    this.promptService = new PromptService(this.$root.prompt)
  },

  computed: {
    submittable: function () {
      if (!this.typeDetails || this.typeDetails.length === 0) {
        return false
      }
      return this.typeDetails.every((details) => {
        return details.hasTimeSelected()
      })
    }
  }

}
</script>



// WEBPACK FOOTER //
// multiTimeStage.vue?03528169